<template>
	<div class="app-calendar">
		<header class="app-calendar__header">
			<button
				class="app-calendar__arrow"
				@click.prevent="changeDisplayedCalendar({yearOp: -1})"
			>
				&lsaquo;&lsaquo;
			</button>
			<button
				class="app-calendar__arrow"
				@click.prevent="changeDisplayedCalendar({monthOp: -1})"
			>
				&lsaquo;
			</button>

				{{ selectedHeader }}

			<button
				class="app-calendar__arrow"
				@click.prevent="changeDisplayedCalendar({monthOp: 1})"
			>
				&rsaquo;
			</button>
			<button
				class="app-calendar__arrow"
				@click.prevent="changeDisplayedCalendar({yearOp: 1})"
			>
				&rsaquo;&rsaquo;
			</button>
		</header>

		<table class="app-calendar__calendar">
			<thead>
				<tr>
					<td
						class="app-calendar__day"
						v-for="(day, index) in daysOfWeek"
						:key="index"
					>
						{{ day }}
					</td>
				</tr>
			</thead>
			<tbody>
				<tr v-for="(week, index) in calendar" :key="index">
					<td
						v-for="(date, index) in week"
						:key="index"
						:class="{'disabled' : isNotValid(date)}"
					>
						<template v-if="date">
							<slot
								name="date"
								:props="{
									date: date,
									select: select,
									isToday: isToday(date),
									isSelected: isSelected(date),
								}"
							>
								<div
									class="app-calendar__date"
									:class="{'app-calendar__date--today' : isToday(date),
										'app-calendar__date--selected' : isSelected(date)}"
									@click.stop="select(date)"
								>
									{{ date.getDate() }}
                                    <div v-if="hasDot(date)" class="app-calendar__circle"></div>
								</div>
							</slot>
						</template>
					</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
export default {
	props: {
		value: Date,
		min: Date,
		max: Date,
		dotDates: {
			type: Array,
			default: () => []
		}
	},

	data () {
		return {
			daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
			displayedCalendar: new Date()
		}
	},

	mounted () {
		if (this.value) this.displayedCalendar = this.value
	},

	computed: {
		selectedHeader () {
			const { year, month } = this.parseDate(this.displayedCalendar)
			const monthText = new Date(year, month, 1).toLocaleString(undefined, { month: 'long' })
			return `${monthText} ${year}`
		},

		calendar () {
			const dates = []
			const { year, month } = this.parseDate(this.displayedCalendar)
			const firstDay = new Date(year, month).getDay()
			const daysInMonth = 32 - new Date(year, month, 32).getDate()

			let date = 1
			for (let week = 0; week <= 5; week++) {
				dates.push(week)
				dates[week] = []

				for (let day = 0; day < 7; day++) {
					if (week === 0 && day < firstDay) {
						dates[week].push(null)
					} else if (date > daysInMonth) {
						break
					} else {
						dates[week].push(new Date(year, month, date))
						date++
					}
				}
			}

			return dates
		}
	},

	methods: {
		isToday (date) {
			return date.toDateString() === new Date().toDateString()
		},

		hasDot (date) {
			return this.dotDates.map(x => new Date(x).toDateString()).includes(date.toDateString())
		},

		isSelected (date) {
			return this.value &&
				date &&
				date.toDateString() === this.value.toDateString()
		},

		select (date) {
			if (!date) return
			if (this.isSelected(date)) return this.$emit('input', null)
			this.$emit('input', date)
		},

		parseDate (date) {
			return {
				year: date.getFullYear(),
				month: date.getMonth(),
				date: date.getDate()
			}
		},

		changeDisplayedCalendar ({ yearOp = 0, monthOp = 0 }) {
			const { year, month } = this.parseDate(this.displayedCalendar)
			const newYear = year + yearOp
			const newMonth = month + monthOp
			this.displayedCalendar = new Date(newYear, newMonth, 1)

			if (this.timeout) clearTimeout(this.timeout)
			this.timeout = setTimeout(() => {
				this.$emit('navigate', this.displayedCalendar)
			}, 1000)
		},

		isNotValid (val) {
			if (!val) return true
			if (this.max && val.getTime() > this.max.setHours(0, 0, 0, 0)) return true
			if (this.min && val.getTime() < this.min.setHours(0, 0, 0, 0)) return true
			return false
		}
	}
}
</script>

<style lang="scss" scoped>
.app-calendar {
	padding: 1em;
	background-color: #fff;
	box-shadow: 0 0 3px $default-light;
	font-size: 1em;
	border-radius: 3px;
	user-select: none;
	display: inline-block;
	color: $default;

	&:hover {
		box-shadow: 0 0 3px $primary;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-evenly;
	}

	&__arrow {
		min-width: 1em;
		font-weight: bold;

		&:hover { color: $primary; }
	}

	&__arrow, &__date {
		cursor: pointer;
		outline: none;
		border: none;
		text-align: center;
		background-color: #fff;
		color: $default;
		font-size: 1em;
		line-height: 1em;
	}

	&__calendar {
		table-layout: fixed;
		border-collapse: collapse;
	}

	&__day {
		text-align: center;
		vertical-align: middle;
		border-bottom: 1px solid #eee;
		padding: 1em 0;
		color: $default-light;
	}

	&__date {
		min-width: 2em;
		min-height: 2em;
		margin: 0.8em .2em;
		border-radius: 50%;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: start;

		&:hover {
			// background-color: #f4f4f4;
            color: $primary;
		}

		&--today {
			color: $accent;
            font-weight: 600;
		}

		&--selected {
			&, &:hover {
				// background-color: lighten($primary, 45%);
				color: $primary;
			}
		}
	}

    &__circle {
        width: 5px;
        height: 5px;
        margin-top: 2px;
        background-color: $danger;
        border-radius: 50%;
    }

	.disabled {
		pointer-events: none;
		opacity: 0.5;
	}
}
</style>



// WEBPACK FOOTER //
// src/components/app-components/AppCalendar.vue